import { useState, useEffect, useRef } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Box } from "@material-ui/core";
import { useHistory } from "react-router-dom";
import OverallPerformanceHeader from "../../components/overall-performance-header/overall-performance-header";
import PerformanceTrend from "../../components/performance-trend/performance-trend";
import ReadinessCard from "../../components/readiness-card/readiness-card";
import OverallPerformanceTabs from "../../components/overall-performance-tabs/overall-performance-tabs";
import Menu from "../../pages/menu/menu";
import {
  getOverallQuizHistory,
  getOverallStudentQuizHistory,
  getQuizHistory,
  getStudentQuizHistory,
  getActivePlans,
  getPreference,
  setPreference
} from "../../actions";
import { useDispatch, useSelector } from "react-redux";
import mixpanel from 'mixpanel-browser';

export default function OverallPerformance() {
  const [performanceData, setPerformanceData] = useState();
  // Shared exam selection: the trend's filter also scopes the readiness
  // card, so the two blocks never describe different exams.
  const [readinessExam, setReadinessExam] = useState(null);
  const [quizHistoryList, setQuizHistoryList] = useState();
  const [quizDetails, setQuizDetails] = useState();
  const [plan, setPlan] = useState({});
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();

  const overallQuizHistory = useSelector((state) => {
    return state.overallQuizHistory?.overallQuizHistory;
  });

  const overallStudentQuiz = useSelector((state) => {
    return state.overallStudentQuiz?.overallStudentQuiz;
  });

  const quizHistory = useSelector((state) => {
    return state.quizHistory?.quizHistory;
  });

  const activePlans = useSelector((state) => {
    return state.getActivePlans;
  });

  // Trend rows (readiness design slice 1): getStudentQuizHistory is the
  // endpoint whose per-quiz score/date/size/category fields are PROVEN by
  // the quiz-history table render; the overallStudentQuiz list above has a
  // different row shape and keeps feeding only the dropdown.
  const studentQuizHistory = useSelector((state) => {
    return state.studentQuizHistory?.studentQuizHistory;
  });

  // Readiness goals (slice 3c). Local state ordered by ARRIVAL: a resolving
  // GET or a save's echoed response each overwrite on change, so the freshest
  // response wins regardless of which redux slice it landed in (a stale save
  // from an earlier visit can't mask a newer fetch).
  const fetchedPreference = useSelector((state) => state.getUserPreference?.preference);
  const preferenceError = useSelector((state) => state.getUserPreference?.errorMsg);
  const savedPreference = useSelector((state) => state.setUserPreference?.preference);
  const [readinessGoals, setReadinessGoals] = useState(null);
  const [goalsLoaded, setGoalsLoaded] = useState(false);
  const sawInitialSaved = useRef(false);
  const sawInitialFetched = useRef(false);

  // Redux slices persist across page visits, so each effect SKIPS the value
  // it mounts with (a previous visit's response, arbitrarily stale) and
  // applies only responses that arrive DURING this mount — the mount GET is
  // always dispatched, so fresh data always comes (QUI#540 re-gate N2).
  // The shape guard is belt-and-braces under the saga's error
  // classification: an error payload must never read as a preference.
  const isPreferenceShape = (p) =>
    Boolean(p) && typeof p === "object" && !(p instanceof Error) && !p.status && !p.errStatus;

  useEffect(() => {
    if (!sawInitialSaved.current) {
      sawInitialSaved.current = true;
      return;
    }
    if (isPreferenceShape(savedPreference)) {
      setReadinessGoals(savedPreference.readinessGoals ?? null);
    }
  }, [savedPreference]);

  // goalsLoaded gates the editor — saving before THIS MOUNT's fetch resolved
  // would wholesale-replace away goals this page has never seen.
  useEffect(() => {
    if (!sawInitialFetched.current) {
      sawInitialFetched.current = true;
      return;
    }
    if (isPreferenceShape(fetchedPreference)) {
      setReadinessGoals(fetchedPreference.readinessGoals ?? null);
      setGoalsLoaded(true);
    }
  }, [fetchedPreference]);

  useEffect(() => {
    const userId = window.localStorage.getItem("userId");
    dispatch(getOverallQuizHistory(userId));
    dispatch(getOverallStudentQuizHistory({ userId, isCompleted: true }));
    dispatch(getStudentQuizHistory(userId));
    dispatch(getPreference(userId));
  }, [dispatch]);

  // Wholesale-replace contract (QLS#511): the service replaces the whole map,
  // so a save must resend every other exam's goal; clearing = omit the key.
  const onSaveGoal = (examType, goalPct) => {
    const userId = window.localStorage.getItem("userId");
    const nextGoals = { ...(readinessGoals || {}) };
    if (goalPct === null) {
      delete nextGoals[examType];
    } else {
      nextGoals[examType] = goalPct;
    }
    dispatch(setPreference({ userId, body: { readinessGoals: nextGoals } }));
  };

  useEffect(() => {
    if (overallQuizHistory) {
      setPerformanceData(overallQuizHistory);
    }
  }, [overallQuizHistory]);

  useEffect(() => {
    if (overallStudentQuiz?.length) {
      setQuizHistoryList(overallStudentQuiz);
      dispatch(getQuizHistory(overallStudentQuiz[0]?.id));
    }
  }, [dispatch, overallStudentQuiz]);

  useEffect(() => {
    if (quizHistory) {
      setQuizDetails(quizHistory);
    }
  }, [quizHistory]);

  useEffect(() => {
    const savedUserId = window.localStorage.getItem("userId");

    if (activePlans?.loading === undefined) {
      dispatch(getActivePlans(savedUserId));
    } else if (activePlans?.plansList) {
      const activePkg = activePlans?.plansList.filter(item => item.iselected)[0];
      setPlan(activePkg || {});
    }
  }, [activePlans, dispatch]);

  const onNavigateToReview = (quizId) => {
    const userInfo = JSON.parse(localStorage.getItem('userdetails'));
    const role = window.localStorage.getItem("role");

    mixpanel.track('OP-review', {
      $name: userInfo?.name,
      email: userInfo?.email,
      role
    });

    history.push(`/result/${quizId}`);
  };

  const gotToSubscription = () => {
    return history.push('subscriptions');
  }

  return (
    <Box component="div" className="body-cont-over overall-perf">
      <Menu />
      <Box component="div" className={`${classes.examContainerWrapper} exam-container-wrapper`}>
        {plan?.plan &&
          <Box component="p" onClick={gotToSubscription} className={`plan-title-ctx ovrl-perf ${plan?.expired ? 'expired' : ''}`}>
           Plan: QLearn {plan?.plan?.category}
          </Box>
        }
        <OverallPerformanceHeader
          navigateToReview={onNavigateToReview}
          performanceData={performanceData}
          quizHistoryList={quizHistoryList}
          quizDetails={quizDetails}
        />
        <PerformanceTrend
          quizzes={studentQuizHistory}
          examChoice={readinessExam}
          onExamChange={setReadinessExam}
        />
        {(goalsLoaded || Boolean(preferenceError)) ? (
          // Held until the preference read RESOLVES (success or error) so the
          // verdict never flashes house-target-then-goal; an errored read
          // fails open to the house target with the editor disabled.
          <ReadinessCard
            quizzes={studentQuizHistory}
            examChoice={readinessExam}
            readinessGoals={readinessGoals}
            onSaveGoal={goalsLoaded ? onSaveGoal : null}
          />
        ) : (
          // D12: reserve the card's vertical space while the preference read is
          // in flight so the later-arriving readiness card doesn't pop in and
          // shove OverallPerformanceTabs down. Only reserved when the student
          // actually has trend data (the card renders null otherwise).
          Array.isArray(studentQuizHistory) && studentQuizHistory.length > 0 && (
            <Box
              component="div"
              data-testid="readiness-skeleton"
              aria-hidden="true"
              className="readiness-card-skeleton"
              style={{ minHeight: 220, margin: 8 }}
            />
          )
        )}
        <OverallPerformanceTabs
          performanceData={performanceData?.categoriesPerformance}
        />
      </Box>
    </Box>
  );
}

const useStyles = makeStyles(() => ({
  examContainer: {
    display: "flex",
  },
  examContainerWrapper: {
    flex: 1,
    margin: 25,
  },
}));
